import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    FaRegBell,
    FaBroadcastTower,
    FaEye,
    FaSearch,
    FaInbox,
    FaBullhorn,
    FaHistory,
    FaTimes,
    FaExclamationTriangle,
    FaInfoCircle,
    FaCheckCircle,
    FaTimesCircle,
    FaTrashAlt,
    FaCheckDouble
} from 'react-icons/fa';
import { showSuccess, showError, showLoading, closeLoading } from '../../../utils/Alert';
import Loader from '../../shared/Loader';
import './StudentNotifications.css';

const StudentNotifications = () => {
    const [loading, setLoading] = useState(true);
    const [notifications, setNotifications] = useState([]);
    const [activeTab, setActiveTab] = useState('alerts'); // 'alerts', 'messages', 'history'
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        fetchNotifications();
    }, []);

    const fetchNotifications = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/notifications', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications(res.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching notifications", err);
            setLoading(false);
        }
    };

    const markAsRead = async (id) => {
        try {
            const token = localStorage.getItem('token');
            await axios.patch(`/api/notifications/${id}/read`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const updated = notifications.filter(n => n._id !== id);
            setNotifications(updated);

            // Sync with header count
            const newUnread = updated.filter(n => !n.isRead).length;
            window.dispatchEvent(new CustomEvent('notificationsRead', { detail: { count: newUnread } }));
        } catch (err) {
            console.error("Dismiss failed", err);
        }
    };

    const handleNotificationClick = (notif) => {
        const title = notif.title.toLowerCase();
        const message = notif.message.toLowerCase();
        const text = (title + ' ' + message).toLowerCase();
        let targetPath = '';

        // 1. Check for Global/Broadcast first (No redirection as per user request)
        if (title.includes('global') || title.includes('announcement') || title.includes('broadcast') ||
            message.includes('global') || message.includes('announcement')) {
            markAsRead(notif._id);
            return;
        }

        // 2. Specific Section Matching with correct priority
        if (text.includes('support') || text.includes('ticket') || text.includes('helpdesk')) {
            targetPath = '/student-dashboard/support';
        } else if (text.includes('catalog') || text.includes('catalogue')) {
            targetPath = '/student-dashboard/catalog';
        } else if (text.includes('job') || text.includes('career') || text.includes('recruiter') || text.includes('placement')) {
            targetPath = '/student-dashboard/jobs';
        } else if (text.includes('interview') || text.includes('application')) {
            targetPath = '/student-dashboard/applications';
        } else if (text.includes('invoice') || text.includes('payment') || text.includes('billing')) {
            targetPath = '/student-dashboard/invoices';
        } else if (text.includes('course') || text.includes('module') || text.includes('lms') || text.includes('lecture') || text.includes('class') || text.includes('session')) {
            targetPath = '/student-dashboard/courses';
        } else if (text.includes('assessment') || text.includes('exam') || text.includes('assignment') || (text.includes('test') && !text.includes('test notification'))) {
            // Only redirect to assessments if it's a real academic term, not just a "test notification"
            targetPath = '/student-dashboard/assessments';
        }

        // Mark as read always
        markAsRead(notif._id);

        if (targetPath) {
            navigate(targetPath);
        }
    };

    const markAllRead = async () => {
        try {
            showLoading("Clearing all notifications...");
            const token = localStorage.getItem('token');
            await axios.patch('/api/notifications/mark-all-read', {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications([]);

            // Sync with header count
            window.dispatchEvent(new CustomEvent('notificationsRead', { detail: { count: 0 } }));

            closeLoading();
            showSuccess("Success", "All notifications dismissed.");
        } catch (err) {
            closeLoading();
            showError("Action Failed", "Could not clear hub.");
        }
    };

    const filteredNotifications = notifications.filter(n => {
        const matchesSearch = n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            n.message.toLowerCase().includes(searchTerm.toLowerCase());

        if (activeTab === 'alerts') return matchesSearch && (n.type === 'Alert' || n.target === 'All' || n.target === 'students');
        if (activeTab === 'messages') return matchesSearch && (n.type === 'Info' || n.type === 'Success');
        return matchesSearch;
    });

    const getIcon = (type) => {
        switch (type) {
            case 'Alert': return <FaExclamationTriangle className="type-icon alert" />;
            case 'Success': return <FaCheckCircle className="type-icon success" />;
            case 'Warning': return <FaTimesCircle className="type-icon warning" />;
            default: return <FaInfoCircle className="type-icon info" />;
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="student-notifications fade-in">
            <div className="hub-header">
                <div className="header-info">
                    <h1 className="hub-title"><FaRegBell /> Communication Hub</h1>
                    <p className="hub-subtitle">Centralized updates, announcements, and tutor communications.</p>
                </div>
                <div className="header-actions">
                    <button className="clear-all-btn" onClick={markAllRead} disabled={notifications.length === 0}>
                        <FaCheckDouble /> Mark All as Read
                    </button>
                </div>
            </div>

            <div className="hub-controls">
                <div className="tab-filters">
                    <button
                        className={`tab-btn ${activeTab === 'alerts' ? 'active' : ''}`}
                        onClick={() => setActiveTab('alerts')}
                    >
                        <FaBullhorn /> System Announcements
                        {notifications.filter(n => (n.type === 'Alert' || n.target === 'All' || n.target === 'students')).length > 0 && (
                            <span className="tab-badge">{notifications.filter(n => (n.type === 'Alert' || n.target === 'All' || n.target === 'students')).length}</span>
                        )}
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'messages' ? 'active' : ''}`}
                        onClick={() => setActiveTab('messages')}
                    >
                        <FaInbox /> Campus Inbox
                        {notifications.filter(n => (n.type === 'Info' || n.type === 'Success')).length > 0 && (
                            <span className="tab-badge">{notifications.filter(n => (n.type === 'Info' || n.type === 'Success')).length}</span>
                        )}
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                        onClick={() => setActiveTab('history')}
                    >
                        <FaHistory /> Archived Updates
                    </button>
                </div>

                <div className="search-bar">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search communications..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="notifications-list">
                {filteredNotifications.length > 0 ? (
                    filteredNotifications.map((notif) => (
                        <div key={notif._id} className={`notif-card ${notif.type.toLowerCase()}`}>
                            <div className="notif-icon-box">
                                {getIcon(notif.type)}
                            </div>
                            <div className="notif-content" onClick={() => handleNotificationClick(notif)} style={{ cursor: 'pointer' }}>
                                <div className="notif-meta">
                                    <span className={`notif-badge ${notif.type.toLowerCase()}`}>{notif.type}</span>
                                    <span className="notif-date">{new Date(notif.createdAt).toLocaleString()}</span>
                                </div>
                                <h3 className="notif-title">{notif.title}</h3>
                                <p className="notif-message">{notif.message}</p>
                            </div>
                            <button className="dismiss-btn" onClick={() => markAsRead(notif._id)} title="Clear Notification">
                                <FaTimes />
                            </button>
                        </div>
                    ))
                ) : (
                    <div className="empty-hub">
                        <div className="empty-icon-wrapper">
                            <FaRegBell />
                        </div>
                        <h3>Your hub is clear</h3>
                        <p>No new notifications in this category at the moment.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StudentNotifications;
